// ANCHOR React
import { useState } from 'react';

// ANCHOR Toast
import { toast } from 'react-hot-toast';

// ANCHOR Base
import { Block } from 'baseui/block';
import { SIZE } from 'baseui/button';
import { ModalButton, ModalFooter } from 'baseui/modal';

// ANCHOR Models
import {
  useCreateFormQuestionAnswerFormSubmitPayload,
  useCreateFormQuestionAnswerFormSuccessDispatch,
} from '@lyon/scoped-models/form/create/CreateFormQuestionAnswerForm';
import {
  useFormQuestionAnswersData,
  useFormQuestionAnswersMutate,
} from '@lyon/scoped-models/form/FormQuestionAnswersData';

// ANCHOR Graph State
import { useAuthToken } from '@lyon/nodes/firebase/auth/auth-token-node';

// ANCHOR Hooks
import { useSentryExceptionHandler } from '@lyon/utils/hooks/useSentryExceptionHandler';
import { usePromise } from '@lyon/utils/hooks/usePromise';

// ANCHOR API
import { createFormQuestionAnswer } from '@lyon/utils/api/forms';

// ANCHOR Components
import { constant } from '@lyon/components/utils/constant';
import { TooltipDisabledButton } from '@lyon/components/utils/TooltipDisabledButton';

const TOOLTIP = {
  block: {
    Block: {
      style: {
        width: 'fit-content',
        display: 'flex',
        justifyContent: 'flex-end',
      },
    },
  },
};

export const LyonCreateFormQuestionAnswerSubmit = constant(() => {
  const token = useAuthToken();
  const catchException = useSentryExceptionHandler();
  const [
    name, feedback, points, formQuestionId,
  ] = useCreateFormQuestionAnswerFormSubmitPayload();
  const setSuccess = useCreateFormQuestionAnswerFormSuccessDispatch();
  const mutate = useFormQuestionAnswersMutate();
  const answers = useFormQuestionAnswersData();

  const mounted = usePromise([
    token, name, feedback, points, formQuestionId,
  ]);

  const isDisabled = answers?.some((answer) => answer.name === name);

  const [loading, setLoading] = useState(false);

  function onClick() {
    if (token && name) {
      setLoading(true);

      const request = (async () => {
        await mounted(createFormQuestionAnswer({
          token,
          name,
          feedback,
          points: Number.parseFloat(points),
          formQuestionId,
        }));

        await mounted(mutate());
      })();

      toast.promise(request, {
        loading: 'Adding form answer',
        success: 'Successfully added form answer',
        error: 'Failed adding form answer',
      }).then(() => {
        setSuccess(true);
        setLoading(false);
      }, (error) => {
        setLoading(false);
        catchException(error);
      });
    }
  }

  return (
    <ModalFooter>
      <Block
        display="flex"
        justifyContent="flex-end"
      >
        <TooltipDisabledButton
          isDisabled={isDisabled}
          content="You've already added this answer!"
          overrides={TOOLTIP}
        >
          <ModalButton
            type="button"
            onClick={onClick}
            size={SIZE.default}
            disabled={isDisabled || !name}
            isLoading={loading}
          >
            Add Answer
          </ModalButton>
        </TooltipDisabledButton>
      </Block>
    </ModalFooter>
  );
});
